function CakeBlockLabel({ label }) {
  return (
    <div className="rounded-xl border-2 border-[#EE2677]/35 bg-[#FCE7F3] px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-wide text-[#C2185B]">Cake</p>
      <p className="text-sm font-extrabold leading-tight text-[#2D2D2D]">{label}</p>
    </div>
  );
}

function IngredientList({ title, subtitle, items, accent }) {
  const divider = accent.divider || "border-[#EBB3A9]/40";
  if (!items?.length) {
    return (
      <div className={`overflow-hidden rounded-2xl border ${accent.border} ${accent.bg}`}>
        <div className={`border-b px-3 py-2 ${divider} ${accent.bg}`}>
          <p className={`text-xs font-extrabold uppercase tracking-wide ${accent.title}`}>{title}</p>
        </div>
        <p className="px-3 py-3 text-xs text-[#888]">None for this selection</p>
      </div>
    );
  }
  return (
    <div className={`overflow-hidden rounded-2xl border ${accent.border} ${accent.bg}`}>
      <div className={`flex items-baseline justify-between gap-2 border-b px-3 py-2 ${divider}`}>
        <p className={`text-xs font-extrabold uppercase tracking-wide ${accent.title}`}>{title}</p>
        <p className={`text-[11px] font-semibold ${accent.subtitle}`}>{subtitle}</p>
      </div>
      <table className="w-full border-collapse text-sm">
        <tbody>
          {items.map((item, i) => (
            <tr key={`${item.name}-${i}`} className={`border-b ${divider} last:border-b-0`}>
              <td
                className={`border-r px-3 py-2 align-top font-semibold leading-tight text-[#2D2D2D] ${divider}`}
              >
                {item.name}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-right align-top font-bold leading-tight text-[#2D2D2D]">
                {item.amount}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BakerIngredientBreakdown({ breakdown, baseFlour, sweetener }) {
  if (!breakdown?.blocks?.length) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
        {breakdown?.error || "Select a flavor with a baking recipe to see ingredients."}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-[#EE2677]/25 bg-gradient-to-b from-white to-[#FFF7FA] p-3 shadow-sm">
      <p className="text-sm font-extrabold uppercase tracking-wide text-[#2D2D2D]">
        Ingredient breakdown
      </p>
      <p className="mt-0.5 text-xs text-[#666]">
        Scaled for your selection — weigh dry and wet separately.
      </p>

      <div className="mt-3 space-y-3">
        {breakdown.blocks.map((block, index) => (
          <div
            key={`${block.label}-${index}`}
            className="overflow-hidden rounded-2xl border border-[#EBB3A9]/50 bg-white"
          >
            <div className="border-b border-[#EBB3A9]/40 bg-[#EE2677]/10 px-3 py-2">
              <CakeBlockLabel label={block.label} />
              <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px] font-semibold">
                <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-amber-900">
                  Base: {block.base_flour || baseFlour || "Maida"}
                </span>
                <span className="rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-sky-900">
                  Sweetener: {block.sweetener || sweetener || "Sugar"}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-2 p-2">
              <IngredientList
                title="Dry"
                subtitle={`${block.dry_grams ?? 0} g`}
                items={block.dry_items}
                accent={{
                  border: "border-amber-200",
                  bg: "bg-amber-50/80",
                  title: "text-amber-900",
                  subtitle: "text-amber-700",
                  divider: "border-amber-200/70",
                }}
              />
              <IngredientList
                title="Wet"
                subtitle={`${block.wet_grams ?? 0} g`}
                items={block.wet_items}
                accent={{
                  border: "border-sky-200",
                  bg: "bg-sky-50/80",
                  title: "text-sky-900",
                  subtitle: "text-sky-700",
                  divider: "border-sky-200/70",
                }}
              />
            </div>

            <div className="flex flex-wrap gap-2 border-t border-[#EBB3A9]/30 bg-[#FAFAFA] px-3 py-2 text-[11px] font-semibold text-[#555]">
              <span>Dry: {block.dry_grams ?? 0} g</span>
              <span>·</span>
              <span>Wet: {block.wet_grams ?? 0} g</span>
              <span>·</span>
              <span className="text-[#C2185B]">Total: {block.total_grams ?? 0} g</span>
            </div>
          </div>
        ))}
      </div>

      <p className="mt-3 rounded-xl bg-[#EE2677]/10 px-3 py-2 text-center text-xs font-bold text-[#C2185B]">
        Grand total input: {breakdown.grand_total_grams || 0} g
      </p>
    </div>
  );
}
